import React, { useEffect, useState } from "react";

import { Button, Col, Row } from "antd";

import { isNil } from "@Utils";

import ExistingCertificatesTable from "@Components/activity/ExistingCertificatesTable";
import { IAssociatedStatuses, IProgressMetric } from "@Components/activity/hooks/useAssociatedStatuses";
import useCPDChartValues from "@Components/activity/hooks/useCPDChartValues";
import useCertificationTableData from "@Components/activity/hooks/useCertificationTableData";
import { useCreateMetadataForCPDReport } from "@Components/activity/mutations";
import { ISearchTerms } from "@Components/activity/types";

import { IActivityDetails } from "@Models/IActivityDetails";
import { IPointsMetadataRequest, IPointsTypes, pointsTypes } from "@Models/IPoints";
import useCMEReport from "./hooks/useCMEReport";
import CPDChart from "@Components/activity/visualisations/CPDChart";
import CMEChart from "@Components/activity/visualisations/CMEChart";
import CertificateChartSkeleton from "./visualisations/CertificationChartSkeleton";
import SessionManager from "@Core/session";

interface ICertificationOverviewProps {
    activities: IActivityDetails[];
    associatedStatusesMap?: Map<string, IAssociatedStatuses>;
    metadataRequest: IPointsMetadataRequest;
    progress: IProgressMetric;
    terms: ISearchTerms;
}

export type ICertificatesFilter = "all" | IPointsTypes;

/**
 * Type predicate for certificates filter
 * @param pointsType
 */
const isCertificatesFilter = (pointsType: string): pointsType is ICertificatesFilter => {
    return [...pointsTypes, "all"].includes(pointsType);
};

/**
 * Certification Overview component
 */
const CertificationOverview = ({
    activities,
    associatedStatusesMap,
    metadataRequest,
    progress,
    terms,
}: ICertificationOverviewProps) => {
    const { userId } = SessionManager.getServiceUser();

    const allActivityInstanceIds = activities?.map((a) => a.activityInstanceId) ?? [];

    const userActivityInstanceIds =
        activities?.filter((activity) => activity.userId === userId)?.map((activity) => activity.activityInstanceId) ??
        [];

    // ** Hooks ** //

    // CPD
    const { createCPDPoints, hasCreatedCPDPoints, isCreatingCPDPoints, resetCPDCreationStatus } =
        useCreateMetadataForCPDReport();
    const { isPointsDataHydrated, pointsData, pointsValuesMap } = useCPDChartValues(activities);

    // CME
    const { createCMEPoints, hasCreatedCMEPoints, isCreatingCMEPoints, resetCMECreationStatus } = useCMEReport();

    // Both
    const { certificationFlags, tableData, columnData } = useCertificationTableData(allActivityInstanceIds);

    // ** State ** //
    const [filter, setFilter] = useState<ICertificatesFilter>("all");

    // ** Effects ** //

    // clear the mutation states when search terms change
    useEffect(() => {
        resetCPDCreationStatus();
        resetCMECreationStatus();
    }, [resetCMECreationStatus, resetCPDCreationStatus, terms]);

    // ** Handles ** //

    /**
     * Handles the filter change
     * @param pointsType
     */
    const handleFilterChange = (pointsType: string) => {
        if (isCertificatesFilter(pointsType)) {
            setFilter(pointsType);
        }
    };

    // ** Variables ** //
    const areVisualisationsHydrated = !isNil(associatedStatusesMap) && isPointsDataHydrated;

    const hasCPDPoints = areVisualisationsHydrated
        ? pointsValuesMap.size > 0 && pointsData?.chartData?.length > 0
        : false;
    const hasCMEPoints = areVisualisationsHydrated
        ? Array.from(associatedStatusesMap.values()).some((a) => a.status === "completed")
        : false;

    const hasSomePoints = hasCPDPoints || hasCMEPoints;
    const hasBothPoints = hasCPDPoints && hasCMEPoints;

    const hasNoFilter = filter === "all";

    const filteredColumnData = hasNoFilter ? columnData : columnData.filter((item) => item.key !== "pointsType");
    const filteredCertificates = hasNoFilter ? tableData : tableData.filter((item) => item.pointsType === filter);

    const cols = hasBothPoints ? 12 : 24;

    // ** Render ** //
    return (
        <section>
            {!areVisualisationsHydrated ? <CertificateChartSkeleton /> : null}
            {hasSomePoints ? (
                <Row gutter={hasBothPoints ? [16, 0] : undefined}>
                    {hasCPDPoints ? (
                        <Col md={cols} xs={24} style={{ marginBottom: "1rem" }}>
                            <CPDChart pointsData={pointsData} />
                            {!hasCreatedCPDPoints ? (
                                <Row>
                                    <Button
                                        className="secondary"
                                        disabled={userActivityInstanceIds.length === 0 || hasCreatedCPDPoints}
                                        loading={isCreatingCPDPoints}
                                        onClick={() =>
                                            createCPDPoints({
                                                req: {
                                                    ...metadataRequest,
                                                    activityInstanceIds: userActivityInstanceIds,
                                                },
                                            })
                                        }
                                    >
                                        {`Create New ${hasCMEPoints ? "CPD " : ""}Certificate`}
                                    </Button>
                                </Row>
                            ) : null}
                        </Col>
                    ) : null}
                    {hasCMEPoints ? (
                        <Col md={cols} xs={24}>
                            <CMEChart progress={progress} />
                            {!hasCreatedCMEPoints ? (
                                <Row>
                                    <Button
                                        className="secondary"
                                        disabled={allActivityInstanceIds.length === 0 || hasCreatedCMEPoints}
                                        loading={isCreatingCMEPoints}
                                        onClick={() =>
                                            createCMEPoints({
                                                activityInstanceIds: allActivityInstanceIds,
                                                startedOn: metadataRequest?.startDate
                                                    ? metadataRequest.startDate
                                                    : null,
                                                endedOn: metadataRequest?.endDate ? metadataRequest.endDate : null,
                                            })
                                        }
                                    >
                                        {`Create New ${hasCPDPoints ? "CME " : ""}Certificate`}
                                    </Button>
                                </Row>
                            ) : null}
                        </Col>
                    ) : null}
                </Row>
            ) : null}
            {certificationFlags.hasSomeReports ? (
                <ExistingCertificatesTable
                    dataSource={filteredCertificates}
                    columnData={filteredColumnData}
                    isFetched={certificationFlags.isHydrated}
                    isFetching={certificationFlags.isFetching}
                    isLoading={certificationFlags.isLoading}
                    onFilterChange={certificationFlags.hasBothReports ? handleFilterChange : undefined}
                />
            ) : null}
        </section>
    );
};

export default CertificationOverview;
